import { ICallInfo, ICallLog } from "../models/Call.model";
import axiosSetup from "../utils/axiosSetup";
import toast from "react-hot-toast";

export const fetchTelecallerLead = async () => {
  const resp = await axiosSetup.get("/telecaller/tallyCallerLead");
  if (resp.data.data == null) {
    return null;
  }
  return resp.data;
};

export const searchLeads = async (query: string, userId: string) => {
  try {
    const response = await axiosSetup.get(
      `/telecaller/searchLeads?query=${query}&userId=${userId}`
    );
    return response.data.data;
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Error searching leads");
    throw error;
  }
};

export const updateTelecallerLead = async (
  leadId: string,
  data: any,
  callLogData?: ICallLog[]
) => {
  try {
    const payload = callLogData ? { ...data, callLogData } : data;
    const response = await axiosSetup.put(
      `/leads/updateLeadData/${leadId}`,
      payload
    );
    return response.data;
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Failed to update lead");
    throw error;
  }
};

export const fetchLogsCount = async (
  _managerId: string,
  startDate?: string,
  endDate?: string
): Promise<{
  byUser: Array<{
    userId: string;
    username: string;
    count: number;
    unpickedCount: number;
    totalDuration: number;
  }>;
}> => {
  try {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const resp = await axiosSetup.get<{
      data: {
        byUser: Array<{
          userId: string;
          username: string;
          count: number;
          unpickedCount: number;
          totalDuration: number;
        }>;
      };
    }>("/telecaller/getTelecallerCount", { params });

    return resp.data.data;
  } catch (err: any) {
    toast.error(err?.response?.data?.message || "Failed to fetch logs count");
    throw err;
  }
};

export const fetchDistrictCounts = async (
  userId: string,
  startDate?: string,
  endDate?: string
): Promise<Array<{ district: string; count: number }>> => {
  try {
    const params: Record<string, string> = { userId };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const resp = await axiosSetup.get(
      "/telecaller/getTelecallerDistrictCount",
      {
        params,
      }
    );
    return resp.data.data.districts;
  } catch (err: any) {
    toast.error(
      err?.response?.data?.message || "Failed to fetch district counts"
    );
    throw err;
  }
};

export const fetchTelecallersForManager = async (managerId: string) => {
  try {
    const resp = await axiosSetup.get<{ data: { telecallers: any[] } }>(
      `/telecaller/getTelecallersForManager/${managerId}`
    );
    return resp.data.data.telecallers;
  } catch (err: any) {
    toast.error(err?.response?.data?.message || "Failed to load telecallers");
    throw err;
  }
};

export const updateTelecallerLocations = async (
  userId: string,
  data: { states: string[]; districts: string[] }
) => {
  try {
    const resp = await axiosSetup.put(
      `/users/updateUserLocations/${userId}`,
      data
    );
    return resp.data.data;
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Failed to update locations");
    throw error;
  }
};

export const fetchRemarks = async (leadId: string) => {
  const response = await axiosSetup.get(`/telecaller/getRemark/${leadId}`);
  return response.data;
};

export const addRemark = async (
  leadId: string,
  text: string,
  callId?: string,
  setFollowUp?: boolean
) => {
  const response = await axiosSetup.post(`/telecaller/addRemark`, {
    leadId,
    text,
    callId,
    setFollowUp,
  });
  return response.data;
};

export const fetchAllBDMUsers = async (
  startDate?: string,
  endDate?: string
) => {
  try {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const resp = await axiosSetup.get("/dashboard/get-all-bdm-users", {
      params,
    });
    return resp.data;
  } catch (err: any) {
    toast.error(err?.response?.data?.message || "Failed to fetch BDM users");
    throw err;
  }
};

export const fetchBDMBoard = async (
  bdmId: string,
  startDate?: string,
  endDate?: string
) => {
  try {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const resp = await axiosSetup.get(`/dashboard/get-bdm-board/${bdmId}`, {
      params,
    });
    return resp.data.data;
  } catch (err: any) {
    toast.error(err?.response?.data?.message || "Failed to fetch BDM board");
    throw err;
  }
};

export const callToLeadNumber = async (data: ICallInfo) => {
  const response = await axiosSetup.post("/telecaller/callfromWeb", data);
  return response.data;
};

export const findLeadByNumber = async (phoneNumber: string) => {
  try {
    const response = await axiosSetup.get(`/telecaller/getLeadByNumber`, {
      params: { number: phoneNumber },
    });
    return response.data;
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Failed to find lead");
    throw error;
  }
};

export const addCallLog = async (
  callLogs: ICallLog[],
  leadId: string,
  showOnDateTelecaller?: string
) => {
  try {
    const response = await axiosSetup.post("/telecaller/add-call-logs", {
      callLogs,
      leadId,
      showOnDateTelecaller,
    });
    return response.data;
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Failed to save call logs");
    throw error;
  }
};

export const getCallLogs = async (leadId: string) => {
  try {
    const response = await axiosSetup.get(
      `/telecaller/get-call-logs/${leadId}`
    );
    return response.data.data;
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Failed to fetch call logs");
    throw error;
  }
};
